import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ClipboardCheck, 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  X, 
  AlertCircle,
  Shield,
  BarChart3,
  FileText,
  Users,
  Wifi,
  Lock
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
// import { updateAgencyRiskScore } from '../services/riskScoreService' // Disabled to prevent RLS policy errors

const Assessment = () => {
  const { user } = useAuth()
  const [hasStarted, setHasStarted] = useState(false)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [isCompleted, setIsCompleted] = useState(false)
  const [score, setScore] = useState(0)
  const [riskLevel, setRiskLevel] = useState('medium')
  const [recommendations, setRecommendations] = useState([])
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [assessmentId, setAssessmentId] = useState(null)

  // Load questions from Supabase
  useEffect(() => {
    loadQuestions()
  }, [])

  const loadQuestions = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('assessment_questions')
        .select(`
          *,
          assessment_categories(name)
        `)
        .eq('is_active', true)
        .order('order_index')
      
      if (error) throw error
      
      // Transform database questions to match component format
      const transformedQuestions = data?.map(q => {
        // Map database category names to component category keys
        const categoryMapping = {
          'Network Security': 'networkSecurity',
          'Data Protection': 'dataProtection', 
          'Access Control': 'accessControl',
          'Incident Response': 'incidentResponse',
          'Security Awareness': 'training',
          'Physical Security': 'physicalSecurity',
          'Compliance & Governance': 'compliance',
          'Business Continuity': 'businessContinuity'
        }
        
        const categoryName = q.assessment_categories?.name || 'General'
        const categoryKey = categoryMapping[categoryName] || 'general'
        
        return {
          id: q.id,
          category: categoryKey,
          priority: 'high',
          question: q.question_text,
          explanation: q.explanation || `This question helps assess your ${categoryName} posture.`,
          recommendation: {
            title: q.recommendation_title || `Improve ${categoryName}`,
            description: q.recommendation_description || `Address this ${categoryName.toLowerCase()} concern to enhance your overall cybersecurity posture.`,
            actions: (() => {
              try {
                return q.recommendation_actions ? JSON.parse(q.recommendation_actions) : ['Review current practices', 'Implement best practices', 'Monitor and maintain', 'Regular assessments']
              } catch (e) {
                console.warn('Failed to parse recommendation_actions:', e)
                return ['Review current practices', 'Implement best practices', 'Monitor and maintain', 'Regular assessments']
              }
            })()
          }
        }
      }) || []
      
      setQuestions(transformedQuestions.length > 0 ? transformedQuestions : staticQuestions)
    } catch (error) {
      console.error('Error loading questions:', error)
      // Fallback to static questions if Supabase fails
      setQuestions(staticQuestions)
    } finally {
      setLoading(false)
    }
  }

  // Static fallback questions
  const staticQuestions = [
    {
      id: 1,
      category: 'dataProtection',
      priority: 'high',
      question: 'Does your office have a written Data Privacy Policy?',
      explanation: 'A Data Privacy Policy is required under the Data Privacy Act of 2012.',
      recommendation: {
        title: 'Create a Data Privacy Policy',
        description: 'Develop and implement a comprehensive data privacy policy compliant with RA 10173.',
        actions: ['Download policy template', 'Customize for your office', 'Get legal review', 'Train staff']
      }
    },
    {
      id: 2,
      category: 'accessControl',
      priority: 'high',
      question: 'Do you have a system for managing user access to sensitive information?',
      explanation: 'Access control is fundamental to protecting sensitive government data.',
      recommendation: {
        title: 'Implement Access Control System',
        description: 'Establish proper user access management with role-based permissions.',
        actions: ['Audit current access', 'Define user roles', 'Implement access controls', 'Regular access reviews']
      }
    },
    {
      id: 3,
      category: 'networkSecurity',
      priority: 'high',
      question: 'Are your networks protected by firewalls and intrusion detection systems?',
      explanation: 'Network security is the first line of defense against cyber threats.',
      recommendation: {
        title: 'Strengthen Network Security',
        description: 'Deploy comprehensive network security measures including firewalls and monitoring.',
        actions: ['Install enterprise firewall', 'Configure intrusion detection', 'Monitor network traffic', 'Regular security updates']
      }
    },
    {
      id: 4,
      category: 'incidentResponse',
      priority: 'medium',
      question: 'Do you have an incident response plan for cybersecurity breaches?',
      explanation: 'Having a plan ensures quick and effective response to security incidents.',
      recommendation: {
        title: 'Develop Incident Response Plan',
        description: 'Create and maintain a comprehensive incident response strategy.',
        actions: ['Draft response procedures', 'Assign response team', 'Conduct drills', 'Update contact lists']
      }
    },
    {
      id: 5,
      category: 'training',
      priority: 'medium',
      question: 'Do your employees receive regular cybersecurity awareness training?',
      explanation: 'Human error is often the weakest link in cybersecurity.',
      recommendation: {
        title: 'Implement Security Training',
        description: 'Provide regular cybersecurity awareness training for all staff.',
        actions: ['Schedule training sessions', 'Create training materials', 'Track completion', 'Test knowledge retention']
      }
    },
    {
      id: 6,
      category: 'compliance',
      priority: 'high',
      question: 'Are you compliant with relevant cybersecurity regulations and standards?',
      explanation: 'Compliance ensures your organization meets legal and regulatory requirements.',
      recommendation: {
        title: 'Ensure Regulatory Compliance',
        description: 'Maintain compliance with all applicable cybersecurity regulations.',
        actions: ['Review regulations', 'Conduct compliance audit', 'Address gaps', 'Document compliance']
      }
    }
  ]

  const currentQuestion = questions[currentQuestionIndex]
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0
  const isLastQuestion = currentQuestionIndex === questions.length - 1

  const handleStart = async () => {
    if (!user) {
      console.error('User not authenticated')
      alert('Please log in to start the assessment.')
      return
    }
    
    try {
      // Create new assessment record with required fields only
      const assessmentData = {
        user_id: user.id,
        title: 'Cybersecurity Assessment',
        status: 'in_progress'
      }
      
      // First try with started_at, fallback without it if column doesn't exist
      let insertResult = null
      let insertError = null
      
      // Try inserting with started_at column first
      const dataWithStartedAt = {
        ...assessmentData,
        started_at: new Date().toISOString()
      }
      
      const result = await supabase
        .from('assessments')
        .insert(dataWithStartedAt)
        .select()
        .single()
      
      insertResult = result.data
      insertError = result.error
      
      // If started_at column error occurs, try without it
      if (insertError && (
        insertError.code === 'PGRST204' || 
        (insertError.message && insertError.message.includes('started_at'))
      )) {
        console.log('started_at column not available, inserting without it')
        
        const fallbackResult = await supabase
          .from('assessments')
          .insert(assessmentData)
          .select()
          .single()
        
        insertResult = fallbackResult.data
        insertError = fallbackResult.error
      }
      
      if (insertError) {
        console.error('Database error creating assessment:', insertError)
        throw insertError
      }
      
      console.log('Assessment created successfully:', insertResult)
      setAssessmentId(insertResult.id)
      setHasStarted(true)
    } catch (error) {
      console.error('Error starting assessment:', error)
      alert('Failed to start assessment. Please try again or contact support.')
      // Don't continue without saving to database as this will cause data loss
    }
  }

  const handleAnswer = async (answer) => {
    const newAnswers = { ...answers, [currentQuestion.id]: answer }
    setAnswers(newAnswers)
    
    // Save answer to database if user is logged in
    if (user && assessmentId) {
      try {
        await supabase
          .from('assessment_responses')
          .upsert({
            assessment_id: assessmentId,
            question_id: currentQuestion.id,
            response_value: answer,
            score: answer === 'yes' ? 1 : 0
          })
      } catch (error) {
        console.error('Error saving answer:', error)
      }
    }
    
    if (isLastQuestion) {
      // Complete assessment
      setTimeout(() => {
        completeAssessment(newAnswers)
      }, 500)
    } else {
      // Move to next question
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1)
      }, 300)
    }
  }

  const handleRestart = () => {
    setHasStarted(false)
    setCurrentQuestionIndex(0)
    setAnswers({})
    setIsCompleted(false)
    setScore(0)
    setRiskLevel('medium')
    setRecommendations([])
    setAssessmentId(null)
  }

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    }
  }

  const completeAssessment = async (finalAnswers) => {
    // Validation: Prevent completion with no questions or answers
    const totalQuestions = questions.length
    const totalAnswers = Object.keys(finalAnswers || {}).length
    
    if (totalQuestions === 0) {
      console.error('Cannot complete assessment: No questions loaded')
      alert('Assessment cannot be completed. No questions were loaded. Please refresh and try again.')
      return
    }
    
    if (totalAnswers === 0) {
      console.error('Cannot complete assessment: No answers provided')
      alert('Assessment cannot be completed. Please answer at least one question.')
      return
    }
    
    if (totalAnswers < totalQuestions) {
      console.warn(`Assessment completed with ${totalAnswers}/${totalQuestions} questions answered`)
    }
    
    // Calculate score based on answered questions only
    const correctAnswers = Object.values(finalAnswers).filter(answer => answer === 'yes').length
    const calculatedScore = totalAnswers > 0 ? Math.round((correctAnswers / totalAnswers) * 100) : 0
    
    // Determine risk level
    let calculatedRiskLevel = 'high'
    if (calculatedScore >= 80) calculatedRiskLevel = 'low'
    else if (calculatedScore >= 60) calculatedRiskLevel = 'medium'
    
    // Generate recommendations for 'no' answers
    const calculatedRecommendations = questions
      .filter(q => finalAnswers[q.id] === 'no')
      .map(q => ({
        id: q.id,
        category: q.category,
        priority: q.priority || 'medium',
        title: q.recommendation?.title || `Improve ${q.category}`,
        description: q.recommendation?.description || 'This area needs attention to improve your security posture.',
        actions: q.recommendation?.actions || ['Review current practices', 'Implement improvements', 'Monitor progress']
      }))
      .filter(Boolean)
    
    // Update UI state immediately so user sees results
    setScore(calculatedScore)
    setRiskLevel(calculatedRiskLevel)
    setRecommendations(calculatedRecommendations)
    setIsCompleted(true)
    
    console.log('Assessment completed:', {
      score: calculatedScore,
      riskLevel: calculatedRiskLevel,
      totalQuestions,
      answeredQuestions: totalAnswers,
      recommendations: calculatedRecommendations.length
    })
    
    // Calculate category scores
    const categoryScores = {}
    const categoryTotals = {}
    
    // Initialize category counters
    questions.forEach(q => {
      const category = q.category
      if (!categoryScores[category]) {
        categoryScores[category] = 0
        categoryTotals[category] = 0
      }
      categoryTotals[category]++
      if (finalAnswers[q.id] === 'yes') {
        categoryScores[category]++
      }
    })
    
    // Convert to percentages
    const categories = {}
    Object.keys(categoryScores).forEach(category => {
      categories[category] = {
        score: categoryTotals[category] > 0 ? Math.round((categoryScores[category] / categoryTotals[category]) * 100) : 0,
        total: categoryTotals[category]
      }
    })

    // Save final assessment to database
    if (user && assessmentId) {
      try {
        // Base update data with columns that definitely exist in the schema
        const baseUpdateData = {
          status: 'completed',
          score: calculatedScore,
          risk_level: calculatedRiskLevel,
          completed_at: new Date().toISOString()
        }
        
        // Extended data for new schema columns
        const extendedUpdateData = {
          ...baseUpdateData,
          total_questions: totalQuestions,
          answered_questions: totalAnswers,
          responses: finalAnswers,
          recommendations: calculatedRecommendations
        }
        
        console.log('Attempting to save assessment results to database...')
        
        // Try to save with extended data first
        let { data, error } = await supabase
          .from('assessments')
          .update(extendedUpdateData)
          .eq('id', assessmentId)
          .select()
        
        let savedWithExtendedData = true
        
        // If extended update fails due to missing columns, use base data only
        if (error && (
          error.message.includes('column') || 
          error.message.includes('does not exist') ||
          error.code === 'PGRST204' ||
          error.code === '42703'
        )) {
          console.warn('Extended columns not available, saving with base data only:', error.message)
          savedWithExtendedData = false
          
          const { data: baseData, error: baseError } = await supabase
            .from('assessments')
            .update(baseUpdateData)
            .eq('id', assessmentId)
            .select()
          
          if (baseError) {
            console.error('Base update also failed:', baseError)
            throw baseError
          } else {
            console.log('Assessment updated successfully with base data:', baseData)
            data = baseData
            error = null
          }
        }
        
        if (error) {
          console.error('Database update error:', error)
          throw error
        }
        
        console.log('✅ Assessment saved successfully to database')
        
        // Store extended data in localStorage for persistence and admin dashboard
        const extendedData = {
          assessmentId,
          userId: user.id,
          agency: user.agency,
          totalQuestions,
          answeredQuestions: totalAnswers,
          responses: finalAnswers,
          recommendations: calculatedRecommendations,
          categoryScores: categories,
          score: calculatedScore,
          riskLevel: calculatedRiskLevel,
          timestamp: new Date().toISOString(),
          savedWithExtendedData
        }
        
        // Store in localStorage for fallback and cross-tab communication
        localStorage.setItem(`assessment_${assessmentId}_extended`, JSON.stringify(extendedData))
        localStorage.setItem('latest_assessment_result', JSON.stringify(extendedData))
        
        if (!savedWithExtendedData) {
          console.log('📝 Extended assessment data stored in localStorage as fallback')
          console.log('💡 To save extended data to database, run the schema migration: fix_assessment_schema.sql')
        }

        // Note: Agency risk assessment functionality has been disabled to prevent RLS policy errors
        console.log('Assessment completed successfully for user:', user.id, 'Agency:', user.agency)

        // Trigger dashboard refresh events
        console.log('Triggering dashboard refresh events...')
        
        // 1. Storage event for cross-tab communication
        localStorage.setItem('assessment_completed', Date.now().toString())
        localStorage.setItem('dashboard_refresh_needed', Date.now().toString())
        
        // 2. Custom event for same-tab communication
        window.dispatchEvent(new CustomEvent('assessmentCompleted', {
          detail: {
            assessmentId: assessmentId,
            score: calculatedScore,
            riskLevel: calculatedRiskLevel,
            timestamp: new Date().toISOString(),
            extendedData: extendedData
          }
        }))
        
        // 3. Remove the storage items after a short delay to allow other tabs to read them
        setTimeout(() => {
          localStorage.removeItem('assessment_completed')
          localStorage.removeItem('dashboard_refresh_needed')
        }, 2000)

      } catch (error) {
        console.error('❌ Error saving assessment:', error)
        
        // Even if database save fails, ensure user sees their results
        console.log('⚠️ Database save failed, but assessment results are still available in the UI')
        
        // Store results in localStorage as backup
        const backupData = {
          assessmentId,
          userId: user?.id,
          agency: user?.agency,
          totalQuestions,
          answeredQuestions: totalAnswers,
          responses: finalAnswers,
          recommendations: calculatedRecommendations,
          score: calculatedScore,
          riskLevel: calculatedRiskLevel,
          timestamp: new Date().toISOString(),
          savedToDatabase: false,
          error: error.message
        }
        
        localStorage.setItem(`assessment_${assessmentId}_backup`, JSON.stringify(backupData))
        localStorage.setItem('latest_assessment_result', JSON.stringify(backupData))
        
        // Show user-friendly message
        alert('Your assessment has been completed successfully! However, there was an issue saving to the database. Your results are preserved and will be available in this session.')
      }
    } else {
      console.log('⚠️ No user session or assessment ID - results available in UI only')
      
      // Store results locally for guest users
      const guestData = {
        totalQuestions,
        answeredQuestions: totalAnswers,
        responses: finalAnswers,
        recommendations: calculatedRecommendations,
        score: calculatedScore,
        riskLevel: calculatedRiskLevel,
        timestamp: new Date().toISOString(),
        isGuestSession: true
      }
      
      localStorage.setItem('guest_assessment_result', JSON.stringify(guestData))
    }
  }

  const getRiskLevelColor = () => {
    switch (riskLevel) {
      case 'low': return 'bg-success-500 text-white'
      case 'medium': return 'bg-warning-500 text-white'
      case 'high': return 'bg-security-500 text-white'
      default: return 'bg-cyber-500 text-white'
    }
  }

  const getRiskLevelIcon = () => {
    switch (riskLevel) {
      case 'low': return '✅'
      case 'medium': return '⚠️'
      case 'high': return '🚨'
      default: return '📊'
    }
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'dataProtection': return FileText
      case 'accessControl': return Lock
      case 'networkSecurity': return Wifi
      case 'incidentResponse': return AlertCircle
      case 'training': return Users
      case 'compliance': return Shield
      case 'physicalSecurity': return Shield
      case 'businessContinuity': return BarChart3
      case 'general': return ClipboardCheck
      default: return ClipboardCheck
    }
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case 'dataProtection': return 'cyber'
      case 'accessControl': return 'security'
      case 'networkSecurity': return 'warning'
      case 'incidentResponse': return 'security'
      case 'training': return 'success'
      case 'compliance': return 'gov'
      case 'physicalSecurity': return 'gov'
      case 'businessContinuity': return 'warning'
      case 'general': return 'cyber'
      default: return 'cyber'
    }
  }

  if (!hasStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card max-w-2xl mx-auto"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-gov-800 mb-4">Assessment Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyber-600">{questions.length}</div>
                  <div className="text-sm text-gov-600">Questions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-success-600">10-15</div>
                  <div className="text-sm text-gov-600">Minutes</div>
                </div>
                <div className="text-center col-span-2 md:col-span-1">
                  <div className="text-2xl font-bold text-warning-600">6</div>
                  <div className="text-sm text-gov-600">Categories</div>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <h3 className="font-semibold text-gov-800">Assessment Categories:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { name: 'Data Protection', icon: FileText, color: 'cyber' },
                  { name: 'Access Control', icon: Lock, color: 'security' },
                  { name: 'Network Security', icon: Wifi, color: 'warning' },
                  { name: 'Incident Response', icon: AlertCircle, color: 'security' },
                  { name: 'Training & Awareness', icon: Users, color: 'success' },
                  { name: 'Compliance', icon: Shield, color: 'gov' }
                ].map((category, index) => {
                  const Icon = category.icon
                  return (
                    <div key={index} className={`flex items-center space-x-2 p-2 rounded-lg bg-${category.color}-50`}>
                      <Icon className={`w-4 h-4 text-${category.color}-600`} />
                      <span className="text-sm font-medium text-gov-700">{category.name}</span>
                    </div>
                  )
                })}
              </div>
            </div>

            <button
              onClick={handleStart}
              disabled={loading || !user}
              className="btn-primary w-full text-lg py-4 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ClipboardCheck size={20} />
              <span>{loading ? 'Loading...' : !user ? 'Please login to start' : 'Start Assessment'}</span>
            </button>
          </motion.div>
        </div>
      </div>
    )
  }

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-8"
          >
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${getRiskLevelColor()}`}>
                <BarChart3 className="w-10 h-10" />
              </div>
            </div>
            <h1 className="text-4xl font-display font-bold text-gov-800 mb-4">
              Assessment Complete!
            </h1>
            <p className="text-xl text-gov-600">
              Here's your cybersecurity readiness report
            </p>
          </motion.div>

          {/* Score Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card mb-8"
          >
            <div className="text-center">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className="text-6xl">{getRiskLevelIcon()}</div>
                <div>
                  <div className="text-4xl font-bold text-gov-800">{Math.round(score)}%</div>
                  <div className={`text-lg font-semibold capitalize ${getRiskLevelColor()}`}>
                    {riskLevel} Risk
                  </div>
                </div>
              </div>
              
              <div className="w-full bg-gov-200 rounded-full h-4 mb-4">
                <div 
                  className={`h-4 rounded-full transition-all duration-1000 ${
                    riskLevel === 'low' ? 'bg-success-500' :
                    riskLevel === 'medium' ? 'bg-warning-500' : 'bg-security-500'
                  }`}
                  style={{ width: `${score}%` }}
                />
              </div>
              
              <p className="text-gov-600">
                {riskLevel === 'low' && 'Excellent! Your organization has strong cybersecurity practices.'}
                {riskLevel === 'medium' && 'Good progress! There are some areas that need improvement.'}
                {riskLevel === 'high' && 'Attention needed! Several critical security gaps were identified.'}
              </p>
            </div>
          </motion.div>

          {/* Recommendations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card mb-8"
          >
            <h2 className="text-2xl font-semibold text-gov-800 mb-6">Recommended Actions</h2>
            {recommendations.length > 0 ? (
              <div className="space-y-4">
                {recommendations.slice(0, 5).map((rec, index) => {
                  const CategoryIcon = getCategoryIcon(rec.category)
                  const categoryColor = getCategoryColor(rec.category)
                  
                  return (
                    <div key={rec.id} className="border border-gov-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className={`w-8 h-8 bg-${categoryColor}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <CategoryIcon className={`w-4 h-4 text-${categoryColor}-600`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gov-800">{rec.title}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              rec.priority === 'high' ? 'bg-security-100 text-security-700' :
                              rec.priority === 'medium' ? 'bg-warning-100 text-warning-700' :
                              'bg-success-100 text-success-700'
                            }`}>
                              {rec.priority} priority
                            </span>
                          </div>
                          <p className="text-gov-600 text-sm mb-3">{rec.description}</p>
                          <div className="space-y-1">
                            {rec.actions.map((action, actionIndex) => (
                              <div key={actionIndex} className="flex items-center space-x-2 text-sm text-gov-700">
                                <Check size={12} className="text-success-600" />
                                <span>{action}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-xl font-semibold text-gov-800 mb-2">Excellent Work!</h3>
                <p className="text-gov-600">You answered 'Yes' to all questions. Your cybersecurity practices are on track!</p>
              </div>
            )}
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={handleRestart}
              className="btn-secondary flex items-center justify-center space-x-2"
            >
              <ClipboardCheck size={16} />
              <span>Take Assessment Again</span>
            </button>
            <button className="btn-primary flex items-center justify-center space-x-2">
              <FileText size={16} />
              <span>Download Report</span>
            </button>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-display font-bold text-gov-800">
              Cybersecurity Assessment
            </h1>
            <span className="text-sm text-gov-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
          </div>
          
          <div className="w-full bg-gov-200 rounded-full h-2">
            <motion.div 
              className="bg-cyber-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
            />
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="card mb-8"
          >
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                {(() => {
                  const CategoryIcon = getCategoryIcon(currentQuestion.category)
                  const categoryColor = getCategoryColor(currentQuestion.category)
                  return (
                    <>
                      <div className={`w-10 h-10 bg-${categoryColor}-100 rounded-lg flex items-center justify-center`}>
                        <CategoryIcon className={`w-5 h-5 text-${categoryColor}-600`} />
                      </div>
                      <span className={`px-3 py-1 text-sm rounded-full bg-${categoryColor}-100 text-${categoryColor}-700 capitalize`}>
                        {currentQuestion.category.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </>
                  )
                })()}
              </div>
              
              <h2 className="text-2xl font-semibold text-gov-800 mb-4">
                {currentQuestion.question}
              </h2>
              
              <p className="text-gov-600">
                {currentQuestion.explanation}
              </p>
            </div>

            {/* Answer Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAnswer('yes')}
                className="flex items-center justify-center space-x-3 p-6 border-2 border-success-200 rounded-lg hover:border-success-400 hover:bg-success-50 transition-colors duration-200 group"
              >
                <div className="w-8 h-8 bg-success-100 rounded-full flex items-center justify-center group-hover:bg-success-200">
                  <Check className="w-5 h-5 text-success-600" />
                </div>
                <span className="text-lg font-medium text-gov-800">Yes</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAnswer('no')}
                className="flex items-center justify-center space-x-3 p-6 border-2 border-security-200 rounded-lg hover:border-security-400 hover:bg-security-50 transition-colors duration-200 group"
              >
                <div className="w-8 h-8 bg-security-100 rounded-full flex items-center justify-center group-hover:bg-security-200">
                  <X className="w-5 h-5 text-security-600" />
                </div>
                <span className="text-lg font-medium text-gov-800">No</span>
              </motion.button>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={previousQuestion}
            disabled={currentQuestionIndex === 0}
            className="flex items-center space-x-2 px-4 py-2 text-gov-600 hover:text-cyber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <ChevronLeft size={16} />
            <span>Previous</span>
          </button>
          
          <button
            onClick={handleRestart}
            className="text-sm text-gov-500 hover:text-security-600 transition-colors duration-200"
          >
            Restart Assessment
          </button>
        </div>
      </div>
    </div>
  )
}

export default Assessment